// Email Settings Component
// Main settings page for managing Gmail connection and sync preferences

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Mail, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Loader2, 
  RefreshCw, 
  Trash2,
  Settings,
  Clock,
  BarChart3,
  Wifi,
  WifiOff,
  Globe
} from 'lucide-react';
import Icon from '@/components/ui/Icon';
import EmailConnectionStatus from './EmailConnectionStatus';
import SyncControls from './SyncControls';
import ConnectEmailPrompt from './ConnectEmailPrompt';
import { useEmailConnection } from '@/hooks/email/useEmailConnection';
import { useEmailProcessing } from '@/hooks/email/useEmailProcessing';
import { getEmailConnection, toggleEmailSync, deleteEmailConnection, saveEmailConnection } from '@/services/emailService';
import { processHistoricalEmails, processDailyEmails, checkN8NWebhookHealth, diagnoseWebhookIssue, testAuthenticatedWebhook, debugAuthConfiguration, testAllAuthFormats, testCORS, testCompleteFlow, testProxyConnection, testProxyWithFullLogging, testAfterN8NFix, debugGmailOAuth } from '@/services/n8n/webhookService';

/**
 * Main Email Settings Component
 * Provides comprehensive Gmail connection management
 */
const EmailSettings = ({ user }) => {
  const [emailConnection, setEmailConnection] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [lastSyncDate, setLastSyncDate] = useState(null);
  const [connectionHealth, setConnectionHealth] = useState('unknown');
  const [totalCampsFound, setTotalCampsFound] = useState(0);
  const [error, setError] = useState(null);

  // Hooks for email functionality
  const {
    isConnected: isEmailConnected,
    syncStatus: emailSyncStatus,
    error: emailError
  } = useEmailConnection();

  const {
    isProcessing,
    lastResult,
    error: processingError,
    processHistorical,
    retryProcessing,
    clearError
  } = useEmailProcessing();

  /**
   * Load current email connection status and settings
   */
  const loadEmailSettings = useCallback(async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      setError(null);

      // Load email connection data
      const connection = await getEmailConnection(user.id);
      setEmailConnection(connection);
      setSyncEnabled(connection?.sync_enabled || false);
      setLastSyncDate(connection?.last_sync_date);
      setTotalCampsFound(connection?.total_camps_found || 0);

      // Check connection health
      if (connection) {
        const health = await checkN8NWebhookHealth();
        setConnectionHealth(health.status);
      }

    } catch (err) {
      console.error('Error loading email settings:', err);
      setError('Failed to load email settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  /**
   * Toggle daily sync on/off
   */
  const handleToggleSync = async (enabled) => {
    if (!user?.id) return;

    try {
      setError(null);
      await toggleEmailSync(user.id, enabled);
      setSyncEnabled(enabled);
      
      // Show success feedback
      console.log(`Daily sync ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      console.error('Error toggling sync:', err);
      setError(`Failed to ${enabled ? 'enable' : 'disable'} daily sync. Please try again.`);
    }
  };

  /**
   * Reconnect Gmail (for expired tokens)
   */
  const handleReconnect = async (oauthData) => {
    console.log('🔵 EmailSettings: handleReconnect called', { 
      hasUser: !!user?.id, 
      hasOauthData: !!oauthData,
      oauthDataKeys: oauthData ? Object.keys(oauthData) : []
    });
    
    if (!user?.id) {
      console.error('❌ EmailSettings: No user ID available');
      return;
    }

    try {
      setError(null);
      
      // Check if OAuth data is provided (from OAuth flow)
      if (oauthData) {
        console.log('✅ EmailSettings: OAuth data received, saving email connection...');
        await saveEmailConnection(user.id, oauthData);
        console.log('✅ EmailSettings: Email connection saved successfully');
        
        // Automatically trigger historical email processing for new connections
        console.log('🚀 EmailSettings: STARTING automatic historical email scan...');
        console.log('🚀 EmailSettings: Calling processHistoricalEmails with user ID:', user.id);
        try {
          const result = await processHistoricalEmails(user.id);
          console.log('✅ EmailSettings: Historical email scan triggered successfully!', result);
        } catch (processingError) {
          console.error('❌ EmailSettings: Auto-trigger FAILED:', processingError);
          console.error('❌ Error details:', {
            message: processingError.message,
            stack: processingError.stack
          });
          // Don't fail the connection - user can manually trigger later
          setError('Email connected successfully, but automatic scan failed. Please click "Rescan Historical" to start processing.');
        }
        
        await loadEmailSettings(); // Refresh display
        console.log('✅ Gmail reconnected successfully');
      } else {
        // If no OAuth data, this means the user clicked connect but OAuth is not configured
        console.warn('⚠️ EmailSettings: No OAuth data provided');
        setError('Gmail OAuth is not configured. Please set up your Google OAuth credentials in the .env.local file.');
      }
    } catch (err) {
      console.error('❌ EmailSettings: Error in handleReconnect:', err);
      setError('Failed to reconnect Gmail. Please try again.');
    }
  };

  /**
   * Handle OAuth errors
   */
  const handleOAuthError = (error) => {
    console.error('Gmail OAuth error:', error);
    setError(error);
  };

  /**
   * Disconnect Gmail completely
   */
  const handleDisconnect = async () => {
    if (!user?.id) return;

    const confirmed = confirm(
      'Disconnect Gmail?\n\n' +
      'You will stop receiving automatic camp email updates. ' + 
      'Your existing camp data will remain saved.'
    );

    if (!confirmed) return;

    try {
      setError(null);
      await deleteEmailConnection(user.id);
      setEmailConnection(null);
      setSyncEnabled(false);
      setLastSyncDate(null);
      setTotalCampsFound(0);
      setConnectionHealth('unknown');
      console.log('Gmail disconnected successfully');
    } catch (err) {
      console.error('Error disconnecting Gmail:', err);
      setError('Failed to disconnect Gmail. Please try again.');
    }
  };

  /**
   * Manual email processing triggers
   */
  const handleRescanHistorical = async () => {
    if (!user?.id) return;

    const confirmed = confirm(
      'Re-scan your email history?\n\n' +
      'This will process the last 2 years of emails again. ' +
      'This may take 1-2 minutes and could find additional camps.'
    );

    if (!confirmed) return;

    try {
      setError(null);
      const result = await processHistoricalEmails(user.id);
      console.log('Historical email processing started:', result);
      
      // Refresh settings to show updated stats
      await loadEmailSettings();
    } catch (err) {
      console.error('Error processing historical emails:', err);
      setError('Failed to start historical email processing. Please try again.');
    }
  };

  const handleSyncNow = async () => {
    if (!user?.id) return;

    try {
      setError(null);
      const result = await processDailyEmails(user.id);
      console.log('Daily email sync started:', result);
      
      // Refresh settings to show updated stats
      await loadEmailSettings();
    } catch (err) {
      console.error('Error syncing emails:', err);
      setError('Failed to start email sync. Please try again.');
    }
  };

  /**
   * Test connection health
   */
  const testConnection = async () => {
    try {
      setError(null);
      const health = await checkN8NWebhookHealth();
      setConnectionHealth(health.status);
      console.log('Connection health test completed:', health);
    } catch (err) {
      console.error('Error testing connection:', err);
      setError('Failed to test connection. Please try again.');
    }
  };

  // Load settings on component mount
  useEffect(() => {
    loadEmailSettings();
  }, [loadEmailSettings]);

  // Handle processing updates
  useEffect(() => {
    if (lastResult) {
      // Refresh settings when processing completes
      loadEmailSettings();
    }
  }, [lastResult, loadEmailSettings]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading email settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="email-settings space-y-6">
      {/* Settings Header */}
      <div className="settings-header">
        <div className="flex items-center gap-3 mb-2">
          <Icon name="mail" size="lg" className="text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-900">Email Settings</h2>
        </div>
        <p className="text-gray-600">
          Manage your Gmail connection for automatic camp tracking
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Connection Status */}
      <EmailConnectionStatus 
        user={user}
        connection={emailConnection}
        onReconnect={handleReconnect}
        onDisconnect={handleDisconnect}
        connectionHealth={connectionHealth}
        onTestConnection={testConnection}
      />

      {/* Sync Controls - Only show if connected */}
      {emailConnection && (
        <SyncControls
          syncEnabled={syncEnabled}
          onToggleSync={handleToggleSync}
          onRescanHistorical={handleRescanHistorical}
          onSyncNow={handleSyncNow}
          isProcessing={isProcessing}
          lastSyncDate={lastSyncDate}
          totalCampsFound={totalCampsFound}
        />
      )}

      {/* Debug Section - Only show in development */}
      {process.env.NODE_ENV === 'development' && (
        <Card className="card-shadow border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-orange-600" />
              Debug Tools
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Development-only tools for debugging webhook issues.
              </p>
              <div className="space-y-2">
                <Button
                  onClick={debugAuthConfiguration}
                  variant="outline"
                  size="sm"
                  className="w-full"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Debug Auth Config
                </Button>
                <Button
                  onClick={diagnoseWebhookIssue}
                  variant="outline"
                  size="sm"
                  className="w-full"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Diagnose Webhook Issue
                </Button>
                <Button
                  onClick={testAuthenticatedWebhook}
                  variant="outline"
                  size="sm"
                  className="w-full"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Test Authenticated Webhook
                </Button>
                <Button
                  onClick={testAllAuthFormats}
                  variant="outline"
                  size="sm"
                  className="w-full"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Test All Auth Formats
                </Button>
                <Button
                  onClick={testCORS}
                  variant="outline"
                  size="sm"
                  className="w-full"
                >
                  <Globe className="h-4 w-4 mr-2" />
                  Test CORS
                </Button>
                <Button
                  onClick={testCompleteFlow}
                  variant="default"
                  size="sm"
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Test Complete Flow
                </Button>
                <Button
                  onClick={testProxyConnection}
                  variant="outline"
                  size="sm"
                  className="w-full border-blue-500 text-blue-600 hover:bg-blue-50"
                >
                  <Wifi className="h-4 w-4 mr-2" />
                  Test Proxy Connection
                </Button>
                <Button
                  onClick={testProxyWithFullLogging}
                  variant="outline"
                  size="sm"
                  className="w-full border-purple-500 text-brand-600 hover:bg-purple-50"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Debug Proxy (Full Logging)
                </Button>
                <Button
                  onClick={testAfterN8NFix}
                  variant="default"
                  size="sm"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Test After N8N Fix
                </Button>
                <Button
                  onClick={debugGmailOAuth}
                  variant="outline"
                  size="sm"
                  className="w-full border-orange-500 text-orange-600 hover:bg-orange-50"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Debug Gmail OAuth
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Connect Prompt - Only show if not connected */}
      {!emailConnection && (
        <ConnectEmailPrompt onConnect={handleReconnect} onError={handleOAuthError} />
      )}

      {/* Sync Statistics - Only show if connected */}
      {emailConnection && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Sync Statistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {totalCampsFound}
                </div>
                <div className="text-sm text-blue-800">Total Camps Found</div>
              </div>
              
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {lastSyncDate ? new Date(lastSyncDate).toLocaleDateString() : 'Never'}
                </div>
                <div className="text-sm text-green-800">Last Sync</div>
              </div>
              
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-brand-600">
                  {syncEnabled ? 'Daily at 9 AM' : 'Disabled'}
                </div>
                <div className="text-sm text-brand-800">Next Sync</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EmailSettings;
